import { Link, isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';

function describe(error: unknown): { title: string; detail: string; status?: number } {
  if (isRouteErrorResponse(error)) {
    return {
      title: error.status === 404 ? 'Page not found' : `Error ${error.status}`,
      detail: error.statusText || (typeof error.data === 'string' ? error.data : ''),
      status: error.status,
    };
  }
  if (error instanceof Error) {
    return { title: 'Something went wrong', detail: error.message };
  }
  return { title: 'Something went wrong', detail: 'An unexpected error occurred.' };
}

export function RouteErrorBoundary() {
  const error = useRouteError();
  const { title, detail, status } = describe(error);
  // eslint-disable-next-line no-console
  console.error('[route error]', error);

  const stack = error instanceof Error ? error.stack : undefined;

  return (
    <div className="flex min-h-screen items-center justify-center bg-app-bg p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>
            {detail || 'The page failed to load. You can try reloading or head back home.'}
          </CardDescription>
        </CardHeader>
        {import.meta.env.DEV && stack && (
          <CardContent>
            <pre className="max-h-48 overflow-auto rounded-md border border-app-border bg-app-surface-2 p-3 text-xs leading-relaxed text-app-text/80">
              {stack}
            </pre>
          </CardContent>
        )}
        <CardFooter className="gap-2 pt-0">
          <Button onClick={() => window.location.reload()}>
            <RefreshCw className="h-4 w-4" />
            Reload
          </Button>
          <Button variant="outline" asChild>
            <Link to={status === 404 ? '/' : '/admin'}>
              <Home className="h-4 w-4" />
              {status === 404 ? 'Go home' : 'Back to dashboard'}
            </Link>
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
